import type { FragmentType } from '@/generated/gql/fragment-masking'
import { useFragment } from '@/generated/gql/fragment-masking'
import { graphql } from '@/generated/gql/gql'
import { capitalise } from '@/helpers/strings'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { Button, Typography, Stack, Radio } from '@mui/material'

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { formatDateText, isServiceTimeOpen } from './helpers'
import MealIcon from './MealIcon'

const canteenServiceDateMealServiceDataFragment = graphql(`
  fragment CanteenServiceDate_MealServiceDataFragment on CCDateMealServiceData {
    date
    lunch {
      meal
      menuIsPublished
      openTime
      closeTime
    }
    breakfast {
      meal
      menuIsPublished
      openTime
      closeTime
    }
    dinner {
      meal
      menuIsPublished
      openTime
      closeTime
    }
  }
`)

interface ServiceDateProps {
  isSelectedDate: boolean
  selectedMeal: string
  timezone: string
  onChange: (date: Date, meal: string) => void
  serviceData: FragmentType<typeof canteenServiceDateMealServiceDataFragment>
}

export function ServiceDate({
  isSelectedDate,
  selectedMeal,
  serviceData,
  timezone,
  onChange,
}: ServiceDateProps) {
  const { lunch, dinner, breakfast, date } = useFragment(
    canteenServiceDateMealServiceDataFragment,
    serviceData,
  )

  const [open, setOpen] = useState(isSelectedDate)
  const { t } = useTranslation()

  useEffect(() => {
    setOpen(isSelectedDate)
  }, [date, isSelectedDate])

  const renderMeal = (meal: string, menuIsOpen: boolean) => (
    <Stack
      paddingRight={1}
      justifyContent="space-between"
      direction="row"
      alignItems="center"
      borderTop={1}
      borderColor="divider">
      <Stack direction="row" alignItems="center">
        <Radio
          onClick={() => onChange(new Date(date), meal)}
          disabled={!menuIsOpen}
          checked={isSelectedDate && selectedMeal === meal}
        />
        <MealIcon type={meal} />
        <Typography paddingLeft={1} variant="body2">
          {capitalise(meal)}
        </Typography>
      </Stack>
      {menuIsOpen ? null : <Typography variant="caption">{t('cc:menuNotReady')}</Typography>}
    </Stack>
  )

  return (
    <Stack direction="column" paddingY={1}>
      <Button
        sx={{ display: 'block' }}
        fullWidth
        size="small"
        variant="text"
        onClick={() => setOpen((prev) => !prev)}>
        <Stack direction="row" justifyContent="space-between" paddingLeft={1}>
          <Typography variant="body2">{formatDateText(new Date(date), timezone)}</Typography>
          {open ? <ExpandLess color="action" /> : <ExpandMore color="action" />}
        </Stack>
      </Button>
      {open ? (
        <Stack direction="column">
          {breakfast
            ? renderMeal(
                'breakfast',
                breakfast.menuIsPublished &&
                  isServiceTimeOpen(breakfast.openTime, breakfast.closeTime),
              )
            : null}
          {lunch
            ? renderMeal(
                'lunch',
                lunch.menuIsPublished && isServiceTimeOpen(lunch.openTime, lunch.closeTime),
              )
            : null}
          {dinner
            ? renderMeal(
                'dinner',
                dinner.menuIsPublished && isServiceTimeOpen(dinner.openTime, dinner.closeTime),
              )
            : null}
        </Stack>
      ) : null}
    </Stack>
  )
}
